import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { AIText } from "@/components/common/AIText";
import { Sparkles, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/** "Why did this company score this way?" - AI explanation grounded in the evidence, cached on the record. */
export function ExplainAssessment({ subjectType, subjectId, initial }) {
  const { can } = useAuth();
  const [exp, setExp] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const [showRefs, setShowRefs] = useState(false);

  useEffect(() => { setExp(initial || null); setShowRefs(false); }, [subjectId, initial]);

  const run = async (force = false) => {
    setBusy(true);
    try { const { data } = await api.post("/ai/explain", { subject_type: subjectType, subject_id: subjectId, force }); setExp(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!can("ai.use")) return null;
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="explain-assessment">
      {!exp ? (
        <Button size="sm" variant="outline" onClick={() => run(false)} disabled={busy} data-testid="explain-assessment-button"><Sparkles className="h-3.5 w-3.5 mr-1" />{busy ? "Reading the evidence…" : "Explain this assessment"}</Button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between"><span className="text-xs font-medium flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-primary" />Why it scored this way</span><Button size="sm" variant="ghost" className="h-7" onClick={() => run(true)} disabled={busy} data-testid="explain-assessment-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></Button></div>
          <AIText text={exp.text} testId="explain-assessment-text" />
          {exp.evidence_refs?.length > 0 && (
            <div>
              <button className="meta underline" onClick={() => setShowRefs((v) => !v)} data-testid="explain-assessment-refs-toggle">{showRefs ? "Hide" : "Show"} evidence references ({exp.evidence_refs.length})</button>
              {showRefs && <ul className="mt-1 space-y-0.5 text-xs">{exp.evidence_refs.map((r) => <li key={r.ref} className="flex gap-2"><span className="font-mono text-muted-foreground shrink-0">[{r.ref}]</span><span className="truncate">{r.claim}</span>{r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="text-primary shrink-0" aria-label="open source"><ExternalLink className="h-3 w-3" /></a>}</li>)}</ul>}
            </div>
          )}
          <div className="meta">{exp.provider}/{exp.model} · ${exp.cost_usd?.toFixed(4)}{exp.cached ? " · cached" : ""}</div>
        </div>
      )}
    </div>
  );
}
